import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, startGoogleLogin } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";

function GoogleButton() {
  return (
    <button
      type="button"
      onClick={startGoogleLogin}
      className="w-full h-11 rounded-md border border-slate-300 bg-white hover:bg-slate-50 transition-colors flex items-center justify-center gap-2.5 text-sm font-medium text-slate-700"
      data-testid="google-login-button"
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      Continue with Google
    </button>
  );
}

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to continue your blueprint.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</p>}
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
        </div>
        <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 h-11" disabled={loading} data-testid="login-submit-button">
          {loading ? "Logging in…" : "Log in"}
        </Button>
        <div className="text-right">
          <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-slate-900 underline" data-testid="forgot-password-link">Forgot password?</Link>
        </div>
      </form>
      <Divider />
      <GoogleButton />
      <p className="text-sm text-slate-500 text-center mt-6">
        No account? <Link to="/register" className="text-slate-900 font-medium underline" data-testid="goto-register-link">Get access</Link>
      </p>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(email, password, name);
    setLoading(false);
    if (res.ok) navigate("/dashboard");
    else setError(res.error);
  };

  return (
    <AuthShell title="Create your account" subtitle="Sign up, then unlock the course with a one-time purchase.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="register-error">{error}</p>}
        <div className="space-y-1.5">
          <Label htmlFor="name">Name</Label>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="register-password-input" />
        </div>
        <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 h-11" disabled={loading} data-testid="register-submit-button">
          {loading ? "Creating…" : "Create account"}
        </Button>
      </form>
      <Divider />
      <GoogleButton />
      <p className="text-sm text-slate-500 text-center mt-6">
        Already have an account? <Link to="/login" className="text-slate-900 font-medium underline" data-testid="goto-login-link">Log in</Link>
      </p>
    </AuthShell>
  );
}

function Divider() {
  return (
    <div className="flex items-center gap-3 my-5">
      <div className="h-px bg-slate-200 flex-1" />
      <span className="text-xs text-slate-400">or</span>
      <div className="h-px bg-slate-200 flex-1" />
    </div>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-md bg-slate-900 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-amber-500" />
          </div>
          <span className="font-display font-bold text-slate-900">High-Ticket Blueprint</span>
        </Link>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
          <h1 className="font-display text-2xl font-bold text-slate-900">{title}</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
